import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { RECOVERY_LINK_OPENED, RECOVERY_LINK_PROBLEM } from '../recoveryDetect'

// PasswordRecoveryGate
//
// Wraps the whole application (see src/index.js). Until now a user who clicked the link in a "reset your password"
// e-mail was simply signed in by Supabase's temporary recovery session and never got a chance to choose a new password.
// This gate intercepts that case and shows a "set a new password" screen BEFORE the application (and therefore
// AuthProvider) is mounted. In every other case it renders its children untouched.
//
//   mode 'open'      -> the normal application
//   mode 'recovery'  -> set-new-password form (link opened, or Supabase raised PASSWORD_RECOVERY)
//   mode 'problem'   -> the reset link was expired / already used: explain and go back to sign-in
//
// The password rules (length, character classes, leaked-password check) are enforced by Supabase Auth on the server;
// the form only checks the minimum length and that both fields match, and shows the server's message otherwise.
// The screens reuse the visual language of LoginPage.jsx (same panel, colours and fonts).

const MIN_LENGTH = 12

function cleanUrl() {
  try { window.history.replaceState(null, '', window.location.pathname) } catch (_e) { /* ignore */ }
}

const DISPLAY = "'Space Grotesk',Inter,sans-serif"
const SANS = "'IBM Plex Sans',sans-serif"
const MONO = "'IBM Plex Mono',monospace"

const S = {
  shell: { display: 'grid', gridTemplateColumns: '1.05fr 1fr', minHeight: '100vh', fontFamily: SANS },
  brand: { position: 'relative', overflow: 'hidden', background: '#0B1F3A', padding: '56px 64px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' },
  bpGrid: { position: 'absolute', inset: 0, opacity: .5, backgroundImage: 'linear-gradient(rgba(93,173,226,.06) 1px,transparent 1px),linear-gradient(90deg,rgba(93,173,226,.06) 1px,transparent 1px)', backgroundSize: '38px 38px' },
  bpMark: { position: 'absolute', right: -130, bottom: -120, opacity: .07, pointerEvents: 'none' },
  bpTop: { position: 'relative', display: 'flex', alignItems: 'center', gap: 14 },
  wm: { fontFamily: DISPLAY, fontWeight: 600, fontSize: 24, color: '#fff', letterSpacing: '-.02em', lineHeight: 1 },
  bpMid: { position: 'relative', maxWidth: 440 },
  bpTag: { fontFamily: MONO, fontSize: 12, letterSpacing: '.16em', textTransform: 'uppercase', color: '#5DADE2', fontWeight: 500, marginBottom: 20 },
  bpH1: { fontFamily: DISPLAY, fontWeight: 600, fontSize: 42, lineHeight: 1.08, letterSpacing: '-.03em', color: '#fff', margin: 0 },
  bpP: { fontSize: 16, color: '#9DB4CE', marginTop: 18, maxWidth: 400 },
  bpFoot: { position: 'relative', display: 'flex', alignItems: 'center', gap: 10, fontSize: 12.5, color: '#5A7595' },
  dot: { width: 5, height: 5, borderRadius: '50%', background: '#2BB673', flexShrink: 0 },
  formPanel: { background: '#F5F8FD', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px 32px' },
  formCard: { width: '100%', maxWidth: 392 },
  fcH2: { fontFamily: DISPLAY, fontWeight: 600, fontSize: 30, letterSpacing: '-.02em', color: '#0B1F3A', margin: 0 },
  fcP: { fontSize: 14.5, color: '#5A6478', marginTop: 8, marginBottom: 24, lineHeight: 1.5 },
  form: { display: 'flex', flexDirection: 'column', gap: 18 },
  label: { fontFamily: MONO, fontSize: 11, fontWeight: 500, letterSpacing: '.1em', textTransform: 'uppercase', color: '#5A6478', display: 'block', marginBottom: 8 },
  input: { width: '100%', boxSizing: 'border-box', padding: '13px 15px', background: '#fff', border: '1.5px solid #E1E9F4', borderRadius: 10, fontFamily: SANS, fontSize: 14.5, color: '#0B1F3A', outline: 'none' },
  submitBtn: (busy) => ({ marginTop: 6, padding: 14, background: busy ? '#0a2d6e' : '#0B3D91', color: '#fff', border: 'none', borderRadius: 10, fontFamily: SANS, fontSize: 14.5, fontWeight: 600, cursor: busy ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%' }),
  errBox: { background: '#fff2f2', border: '1.5px solid #fca5a5', borderRadius: 10, padding: '11px 14px', fontSize: 13, color: '#b91c1c' },
}

function BrandShell({ title, subtitle, children }) {
  return (
    <div style={S.shell}>
      <aside style={S.brand}>
        <div style={S.bpGrid} />
        <svg style={S.bpMark} width="460" height="460" viewBox="0 0 100 100" aria-hidden="true">
          <path d="M50 18 A32 32 0 1 0 82 50 L68 50 A18 18 0 1 1 50 32 Z" fill="#5DADE2" />
          <circle cx="73" cy="50" r="7.5" fill="#5DADE2" />
        </svg>
        <div style={S.bpTop}>
          <svg width="38" height="38" viewBox="0 0 100 100" aria-label="CostSpace">
            <rect width="100" height="100" rx="24" fill="#0B3D91" />
            <path d="M50 18 A32 32 0 1 0 82 50 L68 50 A18 18 0 1 1 50 32 Z" fill="#FFFFFF" />
            <circle cx="73" cy="50" r="7.5" fill="#5DADE2" />
          </svg>
          <span style={S.wm}>Cost<span style={{ color: '#5DADE2' }}>Space</span></span>
        </div>
        <div style={S.bpMid}>
          <div style={S.bpTag}>Project Cost Intelligence</div>
          <h1 style={S.bpH1}>Control Cost.<br />Drive Performance</h1>
          <p style={S.bpP}>Budgets, Forecast, procurement and cash flow — one source of truth for your company</p>
        </div>
        <div style={S.bpFoot}><span style={S.dot} />Protected workspace · CostSpace</div>
      </aside>
      <main style={S.formPanel}>
        <div style={S.formCard}>
          <h2 style={S.fcH2}>{title}</h2>
          <p style={S.fcP}>{subtitle}</p>
          {children}
        </div>
      </main>
    </div>
  )
}

export default function PasswordRecoveryGate({ children }) {
  const [mode, setMode] = useState(
    RECOVERY_LINK_OPENED ? 'recovery' : RECOVERY_LINK_PROBLEM ? 'problem' : 'open'
  )
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') setMode('recovery')
    })
    return () => subscription.unsubscribe()
  }, [])

  async function handleSubmit(e) {
    e.preventDefault()
    setError(null)
    if (password.length < MIN_LENGTH) {
      setError(`Use at least ${MIN_LENGTH} characters.`)
      return
    }
    if (password !== confirm) {
      setError('The two passwords do not match.')
      return
    }
    setSaving(true)
    const { error: updateError } = await supabase.auth.updateUser({ password })
    setSaving(false)
    if (updateError) {
      setError(updateError.message)
      return
    }
    // Success: the recovery session is already a normal signed-in session, so the application opens signed in.
    cleanUrl()
    setPassword('')
    setConfirm('')
    setMode('open')
  }

  function backToSignIn() {
    cleanUrl()
    setMode('open')
  }

  if (mode === 'open') return children

  if (mode === 'problem') {
    return (
      <BrandShell
        title="This link can no longer be used"
        subtitle={'The password-reset link has expired or was already used. Go back to sign in and choose "Forgot password" to get a new one.'}
      >
        <button type="button" style={S.submitBtn(false)} onClick={backToSignIn}>Back to sign in</button>
      </BrandShell>
    )
  }

  return (
    <BrandShell
      title="Set a new password"
      subtitle={`Choose a new password for your account. Use at least ${MIN_LENGTH} characters, with lowercase and uppercase letters, digits and symbols.`}
    >
      <form style={S.form} onSubmit={handleSubmit} noValidate>
        <div>
          <label style={S.label} htmlFor="new-password">New password</label>
          <input id="new-password" style={S.input} type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} disabled={saving} />
        </div>
        <div>
          <label style={S.label} htmlFor="confirm-password">Confirm new password</label>
          <input id="confirm-password" style={S.input} type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} disabled={saving} />
        </div>
        {error && <div style={S.errBox} role="alert">{error}</div>}
        <button type="submit" style={S.submitBtn(saving)} disabled={saving}>
          {saving ? 'Saving…' : 'Save new password'}
        </button>
      </form>
    </BrandShell>
  )
}
